// OGSRenderer — the terminal OGS graph node. Lifts geom_uv + color_uv to 3D Gaussians
// and presents them to the scene's SplatMesh (Spark). Side-effect only (no tensor
// output), so it closes the pipeline: encoder → decoder(s) → plücker → renderer.
//
// N SOURCES: a multi-region bundle wires one source per branch (head, teeth),
// each with its own lifter + pose verts + port pair (graph/branchPorts.ts). All
// sources are lifted, then presented ONCE — so a rig update costs ONE present
// (one SplatMesh write / PLY rebuild) instead of one per branch, AND one
// readback: evaluate() submits every source's lift before awaiting any of them
// (GpuLifter.submitLift / finishLift), then drains them with a single
// Promise.all, so the N mapAsync fences overlap instead of running in series.
// The single-source construction ({lifter, updater, getVerts}) is just
// sources.length === 1. Each source can be switched off live (setSourceEnabled —
// the blueprint branch node's toggle), which is how one region gets isolated.
//
// The heavy, scene-bound objects (GaussianLifter/GpuLifter + SplatMeshUpdater, which
// owns the host SparkRenderer) are created and owned by OGSModel and injected here —
// this node just orchestrates the per-frame lift+present so the graph, not hardcoded
// OGSModel calls, drives the final render. dispose() drops the refs but never tears
// down the injected lifter/updater (the caller outlives the node).

import { nodeTensorToLiftInput } from "../types.ts";
import { concatGaussians } from "./OGSBranch.ts";
import { PackedConcat } from "../../render/packedConcat.ts";
import type {
  DirtySource,
  FrameInputs,
  Node,
  NodeContext,
  NodeTensor,
  PortSpec,
} from "../types";
import type { Gaussians, LiftInput } from "../../inference/lift";

// Structural slices of GaussianLifter / SplatMeshUpdater — kept minimal so tests can
// inject stubs without the WebGPU / Spark chain. lift() is sync on the CPU
// GaussianLifter but async on GpuLifter (readback drain) — evaluate awaits it.
export interface LifterLike {
  lift(verts: Float32Array, geomUv: LiftInput, colorUv: LiftInput): Gaussians | Promise<Gaussians>;
  /** Optional split lift (GpuLifter). Present together or not at all: submitLift
   *  arms the fence, finishLift drains it. Absent on the CPU GaussianLifter,
   *  which has no fence to overlap — evaluate() falls back to plain lift(). */
  submitLift?(geomUv: LiftInput, colorUv: LiftInput): void;
  finishLift?(): Promise<Gaussians>;
}
export interface SplatUpdaterLike {
  updateGaussians(g: Gaussians): void;
}

/** One branch feeding the renderer: its own lifter + pose verts + port pair.
 *  A multi-branch pipeline lifts EVERY source and presents ONCE — one present
 *  and one overlapped readback drain (see the file header). */
export interface OGSRendererSource {
  /** Branch name (head / body) — logs, errors, and the key setSourceEnabled
   *  addresses this source by. */
  name?: string;
  /** Input port pair; see graph/branchPorts.ts for the blueprint-agreed names. */
  ports: [string, string];
  lifter: LifterLike;
  /** Current pose vertices (rig2mesh / frame-scrub output) for THIS branch. */
  getVerts: () => Float32Array;
  /** Packed slot capacity (uv_res²). REQUIRED with 2+ sources: the concatenated
   *  buffer's per-branch base offsets must be fixed for the whole session, so a
   *  branch that hasn't rendered yet still has to reserve its range. */
  capacity?: number;
  /** Region toggle (the blueprint branch node's `enabled`), default on. See
   *  setSourceEnabled — a source that is off is skipped whole. */
  enabled?: boolean;
}

export interface OGSRendererOptions {
  id: string;
  // Input port names, default ["geom_uv", "color_uv"]. Single-source only.
  inputs?: [string, string];
  lifter?: LifterLike;
  updater: SplatUpdaterLike;
  // Current pose vertices (rig2mesh / frame-scrub output) the lift is anchored to.
  getVerts?: () => Float32Array;
  /** Multi-source mode. Mutually exclusive with lifter/getVerts/inputs. */
  sources?: OGSRendererSource[];
}

export class OGSRenderer implements Node {
  readonly id: string;
  readonly type = "ogsRenderer";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[] = [];
  readonly bindings = [];

  private sources: OGSRendererSource[] | null;
  private updater: SplatUpdaterLike | null;
  private concat: PackedConcat | null = null;
  private warnedPartial = false;
  // Whether this renderer's sources lift PACKED words (vs SoA). Decided by the
  // lifters, not by config, so it's observed on the first real present — it
  // picks which flavour of "empty" an all-inactive frame presents.
  private sawPacked = false;
  // Splat count from the most recent lift (debug / tests).
  lastCount = 0;

  constructor(opts: OGSRendererOptions) {
    this.id = opts.id;
    if (opts.sources) {
      if (opts.sources.length === 0) throw new Error(`OGSRenderer "${opts.id}": sources[] is empty`);
      this.sources = opts.sources.map((s) => ({ ...s }));
      if (this.sources.length > 1 && this.sources.some((s) => !s.capacity)) {
        throw new Error(`OGSRenderer "${opts.id}": every source needs a packed `
          + `capacity when there is more than one (fixed slot offsets)`);
      }
    } else {
      const [g, c] = opts.inputs ?? ["geom_uv", "color_uv"];
      if (!opts.lifter || !opts.getVerts) {
        throw new Error(`OGSRenderer "${opts.id}": needs { lifter, getVerts } or sources[]`);
      }
      this.sources = [{ ports: [g, c], lifter: opts.lifter, getVerts: opts.getVerts }];
    }
    this.inputs = this.sources.flatMap((s) => [{ name: s.ports[0] }, { name: s.ports[1] }]);
    this.updater = opts.updater;
    if (this.sources.length > 1) {
      this.concat = new PackedConcat(this.sources.map((s) => s.capacity as number));
    }
  }

  async init(_ctx: NodeContext): Promise<void> {
    // Deps are injected (OGSModel owns the lifter/updater + scene) — nothing to build.
  }

  /** Turn one branch source on/off by name — the blueprint branch node's
   *  `enabled` toggle, applied live. A source that is OFF is skipped before its
   *  inputs are even read: its verts are never requested and its lifter never
   *  runs, so the region costs nothing and its reserved packed slots are simply
   *  cleared (capacity is untouched, so Spark keeps its index→splat map).
   *  Throws on an unknown name — silently ignoring a typo would leave a region
   *  stuck on with the caller believing it toggled. */
  setSourceEnabled(name: string, on: boolean): void {
    if (!this.sources) throw new Error(`OGSRenderer "${this.id}": disposed`);
    const source = this.sources.find((s) => s.name === name);
    if (!source) throw new Error(`OGSRenderer "${this.id}": no source named "${name}"`);
    source.enabled = on;
  }

  async evaluate(
    _frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    if (!this.sources || !this.updater) throw new Error(`OGSRenderer "${this.id}": disposed`);
    const single = this.sources.length === 1;
    // Lift every source that has both its inputs this frame. A source with
    // NEITHER input is simply not rendering (the blueprint's region toggle); a
    // source with exactly one is a wiring bug and fails loudly.
    //
    // TWO PHASES, and the split is the whole point. Phase 1 submits every
    // branch's compute + readback copy without awaiting any of them; phase 2
    // drains them together. Each source owns its own lifter (hence its own
    // readback buffer and `out`), so nothing aliases and the GPU can be working
    // on branch B while branch A's copy is still in flight — N branches cost
    // ONE wall-clock drain instead of N serialized ones. Awaiting inside the
    // loop instead would silently serialize them again.
    // A lifter without the split (the CPU GaussianLifter) runs inline in phase 1
    // and its finisher just hands back the value it already computed.
    const finish: (null | (() => Gaussians | Promise<Gaussians>))[] = [];
    for (const s of this.sources) {
      // Ahead of the inputs on purpose: a source switched off never has its
      // verts read or its lift submitted, whatever arrived on its pins.
      if (s.enabled === false) { finish.push(null); continue; }
      const geom = inbound[s.ports[0]];
      const color = inbound[s.ports[1]];
      if (!geom || !color) {
        if (geom || color || single) {
          throw new Error(`OGSRenderer "${this.id}": missing ${s.ports[0]} / ${s.ports[1]}`);
        }
        finish.push(null);
        continue;
      }
      // Read the verts unconditionally so the accessor is hit once per active
      // source regardless of which lifter flavour this is.
      const verts = s.getVerts();
      const g = nodeTensorToLiftInput(geom);
      const c = nodeTensorToLiftInput(color);
      const l = s.lifter;
      if (typeof l.submitLift === "function" && typeof l.finishLift === "function") {
        l.submitLift(g, c);
        finish.push(() => l.finishLift!());
      } else {
        const done = l.lift(verts, g, c);
        finish.push(() => done);
      }
    }
    const parts: (Gaussians | null)[] = await Promise.all(finish.map((f) => (f ? f() : null)));

    const active = parts.filter((g): g is Gaussians => !!g);
    if (active.length === 0) {
      if (!this.warnedPartial) {
        this.warnedPartial = true;
        console.info(`[ogs] OGSRenderer "${this.id}": no branch produced UVs this frame — presenting an empty splat set`);
      }
      // Every region is toggled off. RETURNING HERE would leave the PREVIOUS
      // frame's splats frozen on screen while onPresented publishes count 0 —
      // the globals and the pixels disagreeing. Present an empty set instead:
      // packed → zero every reserved slot (capacity unchanged, so Spark's
      // index→splat map survives), SoA → an empty Gaussians (count 0 rebuilds
      // the PLY empty).
      const blank = this.concat && this.sawPacked ? this.concat.concat(parts) : concatGaussians([]);
      this.updater.updateGaussians(blank);
      this.lastCount = 0;
      return {};
    }
    const g = single ? active[0] : this.present(parts, active);
    this.updater.updateGaussians(g);
    this.lastCount = g.count;
    return {};
  }

  // Combine several branches' lifts into ONE presentable set. Packed sources go
  // through the fixed-slot concat (persistent buffer, stable index→splat map for
  // Spark's async depth sort); SoA sources through the allocating concatGaussians.
  // A MIX is a misconfiguration — a packed lift leaves its SoA fields stale, so
  // concatenating the two silently renders garbage. Fail loudly instead.
  private present(parts: readonly (Gaussians | null)[], active: readonly Gaussians[]): Gaussians {
    const packedCount = active.filter((g) => !!g.packed).length;
    if (packedCount === 0) return concatGaussians(active);
    if (packedCount !== active.length) {
      throw new Error(
        `OGSRenderer "${this.id}": mixed packed and SoA lift outputs across branches — set packedMode uniformly`,
      );
    }
    this.sawPacked = true;
    return this.concat!.concat(parts);
  }

  dirtyOn(): readonly DirtySource[] {
    // Re-renders whenever its geom/color inputs change — no epoch source of its own.
    return [];
  }

  dispose(): void {
    this.sources = null;
    this.updater = null;
    this.concat = null;
  }
}
